import React from 'react';
import styled, { css } from 'styled-components';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { BiLoader } from 'react-icons/bi';
import { Badge } from '../../components/ui/Badge.jsx';
import { Palette } from '../../components/utils';
import { paymentListStatusBadgeMeta } from './PaymentUtils.js';

const PAYMENT_MODAL_STATUS_TEXT = '#113152';

const PAYMENT_BADGE_COLORS = {
  success: {
    fg: '#2E7D56',
    bg: '#E8F5EF'
  },
  inProgress: {
    fg: '#C2570A',
    bg: '#FFF4E6'
  },
  failed: {
    fg: '#C62828',
    bg: '#FFEBEE'
  },
  reserved: {
    fg: Palette.DARK_ASH,
    bg: Palette.GREY_100
  }
};

const MODAL_ICON_DISC_BG = {
  complete: '#d4ece4',
  failed: '#ffd6d6'
};

const TABLE_SURFACE_BY_VARIANT = {
  complete: PAYMENT_BADGE_COLORS.success,
  inProgress: PAYMENT_BADGE_COLORS.inProgress,
  danger: PAYMENT_BADGE_COLORS.failed,
  archived: PAYMENT_BADGE_COLORS.failed,
  reserved: PAYMENT_BADGE_COLORS.reserved
};

const MODAL_SURFACE_BY_VARIANT = {
  complete: {
    fg: PAYMENT_MODAL_STATUS_TEXT,
    bg: PAYMENT_BADGE_COLORS.success.bg,
    border: PAYMENT_BADGE_COLORS.success.fg
  },
  inProgress: PAYMENT_BADGE_COLORS.inProgress,
  danger: {
    fg: PAYMENT_MODAL_STATUS_TEXT,
    bg: '#ffe9e9',
    border: PAYMENT_BADGE_COLORS.failed.fg
  },
  archived: {
    fg: PAYMENT_MODAL_STATUS_TEXT,
    bg: '#ffe9e9',
    border: PAYMENT_BADGE_COLORS.failed.fg
  },
  reserved: {
    fg: PAYMENT_MODAL_STATUS_TEXT,
    bg: PAYMENT_BADGE_COLORS.reserved.bg,
    border: PAYMENT_BADGE_COLORS.reserved.fg
  }
};

function getStatusSurfaceCss(surface, fallbackSurface) {
  const resolvedSurface = surface ?? fallbackSurface;

  return css`
    ${resolvedSurface.fg && css`color: ${resolvedSurface.fg};`}
    ${resolvedSurface.bg && css`background-color: ${resolvedSurface.bg};`}
    border: 1px solid ${resolvedSurface.border ?? 'currentColor'};
  `;
}

function paymentStatusBadgeSurfaceCss(variant) {
  return getStatusSurfaceCss(TABLE_SURFACE_BY_VARIANT[variant], {
    border: 'currentColor'
  });
}

function paymentStatusBadgeModalSurfaceCss(variant) {
  return getStatusSurfaceCss(MODAL_SURFACE_BY_VARIANT[variant], {
    fg: PAYMENT_MODAL_STATUS_TEXT,
    border: Palette.DARK_ASH
  });
}

const tableStatusBadgeCss = css`
  box-sizing: border-box;
  padding: 0 22px;
  width: 100px;
  font-weight: 600;
`;

const compactStatusBadgeCss = css`
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  line-height: 20px;
  padding: 4px 12px;
  white-space: nowrap;
`;

const ModalInlineFlex = css`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ModalGlyphDisc = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${(p) => p.$bg};
  color: ${PAYMENT_MODAL_STATUS_TEXT};

  svg {
    color: ${PAYMENT_MODAL_STATUS_TEXT};
  }
`;

const MODAL_TRAILING_ICON_BY_VARIANT = {
  complete: {
    Icon: FaCheck,
    bg: MODAL_ICON_DISC_BG.complete
  },
  danger: {
    Icon: FaTimes,
    bg: MODAL_ICON_DISC_BG.failed
  },
  archived: {
    Icon: FaTimes,
    bg: MODAL_ICON_DISC_BG.failed
  }
};

function PaymentModalTrailingIcon({ variant }) {
  if (variant === 'inProgress') {
    return <BiLoader size={16} aria-hidden />;
  }

  const trailingIcon = MODAL_TRAILING_ICON_BY_VARIANT[variant];
  if (!trailingIcon) return null;

  const { Icon, bg } = trailingIcon;

  return (
    <ModalGlyphDisc $bg={bg} aria-hidden>
      <Icon size={10} />
    </ModalGlyphDisc>
  );
}

export function PaymentStatusBadge({ status, t, label, compact = false, modal = false, cssOverride }) {
  const meta = paymentListStatusBadgeMeta(status);
  if (!meta && !compact && label == null) return null;

  const variant = meta?.variant ?? 'reserved';
  const displayLabel = label != null ? label : meta ? t(meta.label) : '';

  const surfaceCss = modal ? paymentStatusBadgeModalSurfaceCss(variant) : paymentStatusBadgeSurfaceCss(variant);
  const baseLayout = cssOverride ?? (compact ? compactStatusBadgeCss : tableStatusBadgeCss);
  const layoutCss = modal ? css`${ModalInlineFlex} ${baseLayout}` : baseLayout;

  return (
    <Badge
      variant={variant}
      cssOverride={css`
        ${surfaceCss}
        ${layoutCss}
      `}
    >
      {modal ? (
        <>
          <span>{displayLabel}</span>
          <PaymentModalTrailingIcon variant={variant} />
        </>
      ) : (
        displayLabel
      )}
    </Badge>
  );
}
